import { useState } from 'react'
import type { User } from '@supabase/supabase-js'

import { LionFull } from '@/components/brand/Lion'
import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { ErrorState } from '@/components/ui/States'
import { supabase, describeError } from '@/lib/supabase'

/**
 * Setting a password after arriving by invitation or by a one-time link.
 *
 * An invitation link signs the person in and then leaves — Supabase never asks
 * them to choose a password, so without this screen the only way back in
 * afterwards is another emailed link. That is a bad daily experience and it was
 * a real hole: the first person invited to this system got in, and then could
 * not get in again.
 *
 * Deciding when to show it is the interesting part. The PKCE flow strips the
 * `type=invite` marker from the URL when it exchanges the code, so the arrival
 * route cannot be read back afterwards. Instead the app records its own flag in
 * user metadata the moment a password is set here or in settings, and this
 * screen appears whenever that flag is missing. It survives the PKCE exchange,
 * it is per-user, and it needs no extra table.
 *
 * Skippable on purpose. Someone who genuinely prefers emailed links should not
 * be held hostage by a modal, and the prompt returns next sign-in.
 */

export const PASSWORD_SET_FLAG = 'password_set'

/** True when this user has never chosen a password through the app. */
export function needsPassword(user: User | null | undefined): boolean {
  if (!user) return false
  return user.user_metadata[PASSWORD_SET_FLAG] !== true
}

export function SetPasswordPrompt({ email, onDone }: { email: string; onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tooShort = password.length > 0 && password.length < 12
  const mismatch = confirm.length > 0 && password !== confirm

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (password !== confirm || password.length < 12) return

    setBusy(true)
    setError(null)

    const { error: updateError } = await supabase.auth.updateUser({
      password,
      data: { [PASSWORD_SET_FLAG]: true },
    })

    setBusy(false)

    if (updateError) {
      setError(describeError(updateError))
      return
    }

    onDone()
  }

  return (
    <main className="grid min-h-dvh place-items-center px-5 py-12">
      <div className="w-full max-w-[24rem]">
        <div className="mb-8 flex flex-col items-center text-center">
          <LionFull width={132} />
          <h1 className="mt-5 font-[family-name:var(--font-heading)] text-[0.9375rem] font-medium">
            Choose a password
          </h1>
          <p className="measure mt-2 text-[length:var(--text-sm)] text-taupe">
            You are signed in as {email}. Set a password so you can get back in without waiting for an email.
          </p>
        </div>

        {error ? <ErrorState className="mb-5" title="Could not set your password" detail={error} /> : null}

        <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-5">
          <Field
            label="Password"
            error={tooShort ? 'At least twelve characters.' : undefined}
            hint="Twelve characters or more, with an uppercase letter and a digit."
          >
            <Input
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Field label="Confirm it" error={mismatch ? 'The two passwords do not match.' : undefined}>
            <Input
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </Field>

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={busy}
            loadingLabel="Saving"
            disabled={password.length < 12 || mismatch}
          >
            Set password
          </Button>
        </form>

        <button
          type="button"
          onClick={onDone}
          className="label mx-auto mt-7 block underline decoration-[var(--color-rule-strong)] underline-offset-4 hover:text-ink"
        >
          Not now — I will use email links
        </button>
      </div>
    </main>
  )
}
